import React, { FC, useContext, useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import { useSnackbar, useWindowDimensions } from '@Iot-Bee/standard-web-library';

import { Card } from '@Iot-Bee/standard-web-library';

import DashboardDataFilter from './components/filter/DashboardDataFilter';
import { Box } from '@mui/material';

import dayjs, { Dayjs } from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { fetchDashboardData, generateFriendlyNamesMap } from './util';
import { DeviceType, estDeviceTypes } from './types';
import { DashboardDeviceData, DashboardFilterData } from './interfaces';
import { DevicesContext } from '../../context/DevicesContext';
import BarchartConsumption from './components/chartViews/BarchartConsumption';
import TotalOverview from './components/chartViews/TotalOverview';
import DashboardOverview from './components/DashboardOverview';
import NoKWHdevicesDashboard from './components/NoKWHdevicesDashboard';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekOfYear);

export const DashboardPage: FC = () => {
  const devicesContext = useContext(DevicesContext);

  if (devicesContext === undefined) {
    throw new Error('DevicesContext is undefined');
  }

  const { devices } = devicesContext;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [devicesData, setDevicesData] = useState<DashboardDeviceData[]>([]);

  const [friendlyNameMap, setFriendlyNameMap] = useState<Map<string, string>>(new Map()); // a map to convert device id's to their user given names

  const [hasESTdevice, setHasESTdevice] = useState<boolean>(false);

  const [dashboardFilterData, setDashboardFilterData] = useState<DashboardFilterData>({
    availableTags: [],
    selectedTags: [],

    availableDevices: [],
    selectedDevices: [],

    workingHoursRange: [300, 1020],

    fromDate: dayjs().startOf('month'),
    toDate: dayjs().endOf('day'),
  });
  const [isLoadedFromSession, setIsLoadedFromSession] = useState<boolean>(false);

  const handleDashboardFilterChange = (
    updater: ((prev: DashboardFilterData) => Partial<DashboardFilterData>) | Partial<DashboardFilterData>
  ) => {
    setDashboardFilterData((prev) => {
      const newState = {
        ...prev,
        ...(typeof updater === 'function' ? updater(prev) : updater),
      };

      // Save the updated state to sessionStorage
      sessionStorage.setItem('dashboardFilterData', JSON.stringify(newState));

      return newState; // Update the state
    });
  };

  // check for sessionStorage dashboardFilterData
  useEffect(() => {
    const data = sessionStorage.getItem('dashboardFilterData');

    try {
      const parsed = JSON.parse(data ?? '{}');
      if (Object.keys(parsed).length > 0) {
        setDashboardFilterData({
          ...parsed,
          fromDate: dayjs(parsed.fromDate).startOf('day'),
          toDate: dayjs(parsed.toDate).endOf('day'),
        });
      }
    } catch (e) {
      console.error('Error parsing sessionstorage dashboardfilterdata', e);
    } finally {
      setIsLoadedFromSession(true);
    }
  }, []);

  useEffect(() => {
    if (devices.length > 0 && isLoadedFromSession) {
      const friendlyNameMap = generateFriendlyNamesMap(devices);
      setFriendlyNameMap(friendlyNameMap);

      // Check if there's an energy-saving tool (EST) device
      const hasEST = devices.some((device) => estDeviceTypes.includes(device.deviceType as DeviceType));
      if (hasEST) {
        setHasESTdevice(true);

        // Fetch dashboard data
        fetchDashboardData(dashboardFilterData.fromDate, dashboardFilterData.toDate, devices)
          .then((newDeviceData: DashboardDeviceData[]) => {
            if (newDeviceData && newDeviceData.length > 0) {
              setDevicesData(newDeviceData);

              const friendlyNames: string[] = [];
              const availableTagsSet: Set<string> = new Set();

              newDeviceData.forEach((device) => {
                availableTagsSet.add(device.tags[0]);
                friendlyNames.push(friendlyNameMap.get(device.sensorId) ?? device.sensorId);
              });
              const tags = Array.from(availableTagsSet);

              handleDashboardFilterChange((prev) => {
                return {
                  ...prev,
                  availableTags: tags,
                  selectedTags: tags,
                  availableDevices: friendlyNames,
                  selectedDevices: friendlyNames,
                };
              });
            } else {
              openSnackbar('info', 'No data available for this time period!');
            }
          })
          .catch((error) => {
            console.error('Error fetching data:', error);
            openSnackbar('error', 'Error fetching data');
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  }, [devices, isLoadedFromSession]);

  const {
    actions: { openSnackbar },
  } = useSnackbar();

  const handleFromDateChange = (from: Dayjs) => {
    handleDashboardFilterChange((prev) => {
      return {
        ...prev,
        fromDate: from,
      };
    });
    handleDateChange(from, dashboardFilterData.toDate);
  };

  const handleToDateChange = (to: Dayjs) => {
    handleDashboardFilterChange((prev) => {
      return {
        ...prev,
        toDate: to,
      };
    });
    handleDateChange(dashboardFilterData.fromDate, to);
  };

  const handleDateChange = (from: Dayjs, to: Dayjs) => {
    setIsLoading(true);
    fetchDashboardData(from, to, devices).then((newDeviceData: DashboardDeviceData[]) => {
      if (newDeviceData && newDeviceData.length > 0) {
        setDevicesData(newDeviceData);
      } else {
        openSnackbar('info', 'No data available for this time period!');
      }
      setIsLoading(false);
    });
  };

  const { screenwidth } = useWindowDimensions();

  return (
    <Stack direction="column" spacing={3}>
      <DashboardOverview devicesData={devicesData} isLoading={isLoading} deviceCount={devicesData.length} />

      {hasESTdevice ? (
        <Box id="dashboard-chart-container">
          <Card title="Consumption Overview">
            <DashboardDataFilter
              devicesData={devicesData}
              dashboardFilterData={dashboardFilterData}
              handleDashboardFilterDataChange={handleDashboardFilterChange}
              friendlyNameMap={friendlyNameMap}
              handleFromDateChange={handleFromDateChange}
              handleToDateChange={handleToDateChange}
            />
            <Box sx={{ padding: screenwidth > 768 ? '11px' : '0px' }}>
              <BarchartConsumption
                data={devicesData.filter((d) =>
                  dashboardFilterData.selectedDevices.includes(friendlyNameMap.get(d.sensorId) ?? d.sensorId)
                )}
                timeRange={dashboardFilterData.workingHoursRange}
                isLoading={isLoading}
                friendlyNameMap={friendlyNameMap}
                fromDate={dashboardFilterData.fromDate}
                toDate={dashboardFilterData.toDate}
              />
              <TotalOverview
                data={devicesData.filter((d) =>
                  dashboardFilterData.selectedDevices.includes(friendlyNameMap.get(d.sensorId) ?? d.sensorId)
                )}
                timeRange={dashboardFilterData.workingHoursRange}
                isLoading={isLoading}
              />
            </Box>
          </Card>
        </Box>
      ) : (
        <NoKWHdevicesDashboard />
      )}
    </Stack>
  );
};

export default DashboardPage;
